import { GoogleCalendar, IosCalendar, Outlook } from '@florencelabs/icons';
import { Modal, ModalCloseButton } from '@florencelabs/modal-2';
import {
  google,
  ics,
  outlook,
  CalendarEvent,
  outlookMobile,
} from 'calendar-link';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsDesktop } from '@provider-directory/hooks/useBreakpoints';

export interface AddToCalendarModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  event: CalendarEvent | null;
}

export const AddToCalendarModal = ({
  event,
  isOpen,
  setIsOpen,
}: AddToCalendarModalProps): ReactElement => {
  const [t] = useTranslation();
  const isDesktop = useIsDesktop();

  return (
    <Modal
      isDismissable={true}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      className={
        'absolute bottom-[-2rem] w-full p-8 pb-20 tablet:p-10 tablet:px-14 desktop:relative desktop:w-fit desktop:px-10 desktop:pb-10'
      }
      data-testid={'add-to-calendar-modal'}
    >
      <div className={'w-full text-right'}>
        <ModalCloseButton
          slot={null}
          onPress={() => {
            setIsOpen(false);
          }}
          className={'relative ml-auto inline-block h-6 w-6 !py-0'}
          ariaLabel={t('close')}
        />
      </div>
      <div className={'w-full py-8 text-left font-serif text-display-large'}>
        {t('schedule.selectYourCalendar')}
      </div>
      <div className={'flex w-full flex-col gap-3'}>
        <button
          className={
            'flex h-12 w-full flex-row rounded-xl border px-3 py-[10px] desktop:w-[19.5rem]'
          }
          data-testid={'add-to-google-calendar-button'}
          onClick={() => {
            event && window.open(google(event), '_blank');
          }}
        >
          <GoogleCalendar />
          <span className={'grow text-center font-semibold'}>
            {t('schedule.calendars.googleCalendar')}
          </span>
        </button>
        <button
          className={
            'flex h-12 w-full flex-row rounded-xl border px-3 py-[10px] desktop:w-[19.5rem]'
          }
          data-testid={'add-to-apple-calendar-button'}
          onClick={() => {
            event && window.open(ics(event), '_blank');
          }}
        >
          <IosCalendar />
          <span className={'grow text-center font-semibold'}>
            {t('schedule.calendars.iosCalendar')}
          </span>
        </button>
        <button
          className={
            'flex h-12 w-full flex-row rounded-xl border px-3 py-[10px] desktop:w-[19.5rem]'
          }
          data-testid={'add-to-outlook-calendar-button'}
          onClick={() => {
            event &&
              window.open(
                isDesktop ? outlook(event) : outlookMobile(event),
                '_blank',
              );
          }}
        >
          <Outlook />
          <span className={'grow text-center font-semibold'}>
            {t('schedule.calendars.outlook')}
          </span>
        </button>
      </div>
    </Modal>
  );
};
